import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Check } from 'lucide-react';

/**
 * Smart Tool: "Which University is Right for You?"
 * Lightweight 3-question funnel for blog/info pages.
 * Final step deep-links to /visa-eligibility/ pre-loaded.
 */
const questions = [
  {
    key: 'destination',
    label: 'Where do you want to study?',
    options: [
      { value: 'germany', label: 'Germany' },
      { value: 'uk', label: 'UK' },
      { value: 'canada', label: 'Canada' },
      { value: 'australia', label: 'Australia' },
      { value: 'usa', label: 'USA' },
      { value: 'ireland', label: 'Ireland' },
      { value: 'dubai', label: 'Dubai / UAE' },
      { value: 'open', label: 'Open to suggestions' },
    ],
  },
  {
    key: 'level',
    label: 'What level of study?',
    options: [
      { value: 'masters', label: 'Masters' },
      { value: 'bachelors', label: 'Bachelors' },
      { value: 'diploma', label: 'PG Diploma' },
      { value: 'mba', label: 'MBA' },
    ],
  },
  {
    key: 'priority',
    label: "What's your top priority?",
    options: [
      { value: 'low-cost', label: 'Low Cost' },
      { value: 'pr', label: 'PR-Friendly Pathway' },
      { value: 'top-ranked', label: 'Top-Ranked University' },
      { value: 'fast-process', label: 'Quick Admission Process' },
    ],
  },
];

const SmartToolWidget = ({ variant = 'sidebar' }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const handlePick = (value) => {
    const q = questions[step];
    const next = { ...answers, [q.key]: value };
    setAnswers(next);
    if (step < questions.length - 1) setStep(step + 1);
    else setStep(step + 1); // done
  };

  const isDone = step >= questions.length;

  const ctaHref = isDone
    ? `/study-abroad/visa-eligibility/?source=smart-tool-widget&prefill_country=${encodeURIComponent(answers.destination || '')}&prefill_level=${encodeURIComponent(answers.level || '')}&prefill_priority=${encodeURIComponent(answers.priority || '')}`
    : '#';

  const wrapperBase = variant === 'sidebar'
    ? 'bg-gradient-to-br from-[#003087] to-[#001F5C] rounded-2xl p-5 text-white border border-[#C5A028]/30 shadow-xl'
    : 'bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-6 lg:p-8 text-white border border-[#C5A028]/30';

  return (
    <div className={wrapperBase} data-testid="sa-smart-tool-widget">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-[#C5A028] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-black" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#C5A028] font-bold">Smart Tool</span>
      </div>

      {!isDone ? (
        <>
          <h3 className="text-base lg:text-lg font-bold font-syne text-white mb-1 leading-tight">
            Which University is Right for You?
          </h3>
          <p className="text-[11px] text-slate-300 mb-4">{step + 1} of {questions.length}</p>

          <div className="text-sm font-semibold text-white mb-3">{questions[step].label}</div>
          <div className="grid grid-cols-2 gap-2">
            {questions[step].options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePick(opt.value)}
                className="text-xs text-left bg-white/5 hover:bg-[#C5A028] hover:text-black border border-white/10 hover:border-[#C5A028] rounded-lg px-3 py-2 transition-all"
                data-testid={`smart-tool-opt-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5 mt-4 justify-center">
            {questions.map((q, i) => (
              <span key={`dot-${q.id ?? q.question ?? i}`} className={`h-1 rounded-full transition-all ${
                i < step ? 'w-6 bg-[#C5A028]' : i === step ? 'w-6 bg-white' : 'w-2 bg-white/20'
              }`} />
            ))}
          </div>
        </>
      ) : (
        <div data-testid="sa-smart-tool-done">
          <div className="w-9 h-9 rounded-full bg-emerald-400/20 flex items-center justify-center mb-3">
            <Check className="w-4 h-4 text-emerald-300" />
          </div>
          <h3 className="text-base font-bold font-syne text-white mb-2">Your shortlist is ready</h3>
          <p className="text-xs text-slate-300 mb-4 leading-relaxed">
            Based on your answers ({answers.destination !== 'open' ? answers.destination : 'flexible'} · {answers.level} · {(answers.priority || '').replace('-', ' ')}),
            run our full eligibility checker for a personalised university shortlist.
          </p>
          <Link
            to={ctaHref}
            className="block text-center bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3 rounded-full transition-all"
            data-testid="sa-smart-tool-cta"
          >
            Get My Shortlist <ArrowRight className="w-4 h-4 inline ml-1" />
          </Link>
          <button
            type="button"
            onClick={() => { setStep(0); setAnswers({}); }}
            className="block w-full text-center text-[11px] text-slate-400 hover:text-white mt-3"
          >
            ← Start over
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartToolWidget;
